import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../configs/WebServiceUrl";

const EmailForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=password
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [expiryTime, setExpiryTime] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // ⏱ Timer logic based on expiryTime from backend
  useEffect(() => {
    if (step === 2 && expiryTime) {
      const timer = setInterval(() => {
        const diff = Math.max(
          0,
          Math.floor((new Date(expiryTime) - new Date()) / 1000)
        );
        setTimeLeft(diff);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, expiryTime]);

  // Step 1: Send OTP
  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/general/forgot-password`, {
        mobileNoOrEmail: email,
      });

      if (res.data?.result_code === 1) {
        alert("OTP sent successfully!");
        const expires = res.data?.data?.expires_at;
        setExpiryTime(expires);
        setStep(2);
      } else {
        alert(res.data?.result_message || "Failed to send OTP");
      }
    } catch (err) {
      alert("Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Resend OTP
  const resendOtp = async () => {
    try {
      const res = await axios.post(`${BASE_URL}/general/forgot-password`, {
        mobileNoOrEmail: email,
      });

      if (res.data?.result_code === 1) {
        alert("OTP resent successfully!");
        const expires = res.data?.data?.expires_at;
        setExpiryTime(expires);
      } else {
        alert(res.data?.result_message || "Failed to resend OTP");
      }
    } catch (err) {
      alert("Error resending OTP");
    }
  };

  // Step 2: Verify OTP
  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/general/verify-otp`, {
        email,
        otp,
        otp_type: "email_fg",
      });

      if (res.data?.result_code === 1) {
        alert("OTP verified successfully!");
        setStep(3);
      } else {
        alert(res.data?.result_message || "Invalid OTP");
      }
    } catch (err) {
      alert("Error verifying OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/general/reset-password`, {
        email,
        password: newPassword,
      });

      if (res.data?.result_code === 1) {
        alert("Password reset successful!");
        navigate("/login");
      } else {
        alert(res.data?.result_message || "Failed to reset password");
      }
    } catch (err) {
      alert("Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container d-flex justify-content-center align-items-center ">
      <motion.div
        className="card shadow p-4"
        style={{ width: "100%", maxWidth: "400px", borderRadius: "16px" }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <AnimatePresence mode="wait">
          {/* Step 1: Email */}
          {step === 1 && (
            <motion.form
              key="email"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={sendOtp}
            >
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Enter your registered email
                </label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>
            </motion.form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <motion.form
              key="otp"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={verifyOtp}
            >
              <div className="mb-3">
                <label className="form-label fw-semibold">Enter OTP</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </div>

              <div className="text-center mb-3">
                {timeLeft > 0 ? (
                  <motion.p
                    className="mt-2 text-muted fw-semibold"
                    key={timeLeft}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                  >
                    ⏱ OTP expires in{" "}
                    <span className="text-danger">{timeLeft}s</span>
                  </motion.p>
                ) : (
                  <motion.button
                    key="resend"
                    type="button"
                    className="btn btn-link p-0 text-primary fw-bold"
                    onClick={resendOtp}
                  >
                    🔁 Resend OTP
                  </motion.button>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-success w-100"
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
            </motion.form>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <motion.form
              key="password"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={resetPassword}
            >
              {/* New Password */}
              <div className="mb-3 position-relative">
                <label className="form-label fw-semibold">New Password</label>
                <div className="position-relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control pe-5"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <i
                    className={`fa ${
                      showNewPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#6c757d",
                    }}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  ></i>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="mb-3 position-relative">
                <label className="form-label fw-semibold">
                  Confirm Password
                </label>
                <div className="position-relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control pe-5"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <i
                    className={`fa ${
                      showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#6c757d",
                    }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  ></i>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-success w-100"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Set Your Password"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="text-center mt-3">
          <small
            style={{ cursor: "pointer", color: "#007bff" }}
            onClick={() => navigate("/login")}
          >
            ← Back to Login
          </small>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailForgotPassword;
